import React, { useEffect, useState } from 'react';
import { Form } from 'semantic-ui-react';
import { Modal, VALIDATORS, generateResolver, yup, Button, FileUtils, FILE_CATEGORIES, TextForm } from 'dyl-components';

import { useForm, Controller, FormProvider } from 'react-hook-form';

import { useDispatch, useSelector } from 'react-redux';

import textTemplateActions from 'actions/text_templates';
import FullScreenModalPopUp from 'shared/FullScreenModalPopUp';
import userActions from 'actions/user';

const TextTemplateModal = ({
    categoryOptions,

    onPreview,
    open,
    mode,
    isSaving,
    onClose,
    id,
    duplicate_id,
    onUpdate,
    onCreate
}) => {
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

    const methods = useForm({
        defaultValues:{
            text: '',
            attachments: [],
        },
        mode: 'onChange',
        resolver: generateResolver({
            name: VALIDATORS.TEXT_TEMPLATE_NAME(true).required('This field is required'),
            category: yup.number().required('This field is required'),
            attachments: FileUtils.getFilesizeValidator(FILE_CATEGORIES.TEXT)
        })
    });

    const { control, formState: { isValid , isDirty }, getValues, setValue, setError, trigger } = methods;

    const dispatch = useDispatch();

    const isReading = useSelector(state => state.text_template.isReadingTemplate);

    useEffect(() => {
        if (id || duplicate_id) {
            dispatch(textTemplateActions.getTemplate(id || duplicate_id)).then(({
                name,
                attachments,
                message,
                template_category
            }) => {
                setValue('attachments', attachments?.data.map(attachment => ({
                    path: attachment.name,
                    id: attachment.id,
                    file_id: attachment.file_id,
                    name: attachment.name,
                    size: attachment.size,
                    url: attachment.url,
                })) || []);
                setValue('category', template_category?.id);
                setValue('text', message || '');
                setValue('name', duplicate_id ? `Duplicate - ${name}` : name);

                trigger('name');
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const preview = () => {
        const { text, attachments } = getValues();
        onPreview({ text, attachments });
    }

    const close = () => {
        onClose();
    }

    const save = () => {
        const { name, ...state } = getValues();
        const payload = {
            name: name ? name.trim() : '',
            ...state
        }
        dispatch(userActions.getProductLimits());
        if (id) {
            onUpdate(id, payload).then(() => {
                close();
            }).catch((error) => {
                if (error?.response?.data?.Code === 409) {
                    if (setError) {
                        setError("name", { type: "unique", message: "Template name already exists!" });
                    }
                }
            });
        } else {
            onCreate(payload).then(() => {
                close();
            }).catch((error) => {
                if (error?.Code === 409) {
                    if (setError) {
                        setError("name", { type: "unique", message: "Template name already exists!" });
                    }
                }
            });
        }
    }
    
    return (
        <React.Fragment>
            <Modal open={open} onClose={() => {
                isDirty
                    ? setIsConfirmationOpen(true)
                    : close();
            }}>
                <Modal.Header>{mode || 'Create'} Text Template</Modal.Header>
                <Modal.Content>
                    <Form loading={isReading || isSaving}>
                        <Form.Group style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                            <Controller
                                name="name"
                                control={control}
                                defaultValue={''}
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <Form.Input
                                        width={10}
                                        label="Template Name"
                                        onChange={onChange}
                                        value={value}
                                        error={error && error.message && {
                                            content: error.message,
                                            pointing: 'below'
                                        }}
                                        required
                                    />
                                )}
                            />
                            <Controller
                                name="category"
                                control={control}
                                defaultValue={null}
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <Form.Select
                                        width={5}
                                        label="Category"
                                        onChange={(_, { value: selected }) => { onChange({ target: { name: 'category', value: selected } }) }}
                                        value={value}
                                        error={error && error.message && {
                                            content: error.message,
                                            pointing: 'below'
                                        }}
                                        required

                                        placeholder="Select Category"
                                        selectOnBlur={false}
                                        options={categoryOptions}
                                    />
                                )}
                            />
                        </Form.Group>
                        <FormProvider {...methods}>
                            <TextForm />
                        </FormProvider>
                    </Form>
                </Modal.Content>
                <Modal.Actions 
                    hasSaveButton
                    onSave={save}
                    saveDisabled={!isValid}
                    saveOptions={{loading: isSaving}}
                >
                    {!isSaving && <Button basic onClick={preview}>Preview</Button>}
                </Modal.Actions>
            </Modal>
            <FullScreenModalPopUp
                header={"Changes not saved"}
                subheader={"Are you sure you want to exit?"}
                isOpen={isConfirmationOpen}
                onConfirm={() => {
                    close();
                    setIsConfirmationOpen(false);
                }}
                onFormClose={() => setIsConfirmationOpen(false)}
                closeIcon={false}
            />
        </React.Fragment>
    );
};

export default TextTemplateModal;
